import React, { useState, useRef } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Typography,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Badge,
  Button,
  TextField,
  Theme,
  Tooltip,
  Stack,
  ButtonBase,
  keyframes,
  Modal,
  Fade,
  Backdrop,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import BugReportIcon from "@mui/icons-material/BugReport";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import logo from "../../../assets/Vector.svg";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import distributorIcon from "../../../assets/Distributor.svg";
import { RootState } from "../../../redux/store";
import MenuIcon from "@mui/icons-material/Menu";
import { BalanceIcon, CartIcon } from "../../icons/CustomIcons";
import AvatarMenu from "./AvatarMenu";
import LanguageSelector from "./LanguageSelector";
import NotificationDrawer from "../../molecules/NotificationDrawer";
import SalesNotificationMenu from "../../molecules/SalesNotificationMenu";
import CustomSearchDropdown from "../../atoms/CustomSearchDropdown";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useAppDispatch } from "../../../redux/store";
import { fetchCartItems } from "../../../redux/slices/cartSlice";
import { fetchSalesCartItems } from "../../../redux/slices/salesCartSlice";
import { getCustomerList, setSalesSession } from "../../../redux/apis/sales/profileApis";
import { getCustomerBalance } from "../../../redux/apis/sales/accountApis";
import { getRetailerCustomerBalance } from "../../../redux/apis/retailer/accountReceivableApis";
import { getSalesTradeShow } from "../../../redux/apis/sales/salesTradeShowApis";
import { getRetailerTradeShow } from "../../../redux/apis/retailer/tradeShowApis";
import { setSelectedCustomer, updateSessionCustomer, setMultipleStores, logout, setAuthFromSwitchStore, setShowTradeShow, setActiveTradeShows, setPriceBook, setAllowShowBalance } from "../../../redux/slices/authSlice";
import { fetchNotificationCount } from "../../../redux/slices/notificationSlice";
import { hasMultipleStore, switchStore, getPriceBook, getAllowedShowBalance } from "../../../redux/apis/retailer/dashboardApis";
import { authLogout } from "../../../redux/apis/authAPIs";
import { clearCart } from "../../../redux/slices/cartSlice";
import { clearSalesCart } from "../../../redux/slices/salesCartSlice";
import { clearDashboardData } from "../../../redux/slices/dashboardSlice";
import { clearSalesDashboardData } from "../../../redux/slices/salesDashboardSlice";
// import { validateCartForCheckout } from "../../../utils/cartValidationUtils";
import Story from "../../../pages/admin/story/Story";
import Stories from "../../../pages/retailer/stories/Stories";
import { getStories } from "../../../redux/apis/retailer/stories";
import ViewStory from "../../../assets/ViewStory.svg";
import { getLatestVersion } from "../../../redux/apis/dashboardApis";
  // import AddIcon from '../../../assets/AddStory.svg'
// import { showErrorToast } from "../../../utils/toastUtils";

// Responsive height of the floating navbar card (vertical layout). Figma spec
// is 68px on large screens; it steps down on laptop / tablet widths.
// AdminLayout imports this to compute the content-area height, so the two
// always stay in sync.
export const FLOATING_NAVBAR_HEIGHTS = { xs: 56, md: 60, lg: 68 };

interface NavbarProps {
  onDrawerToggle?: () => void;
  onTabChange?: (tab: number) => void;
  // Hover open/close for the details popup (vertical layout): fired when the
  // pointer enters / leaves a details tab.
  onTabHover?: (tab: number) => void;
  onTabHoverEnd?: () => void;
  selectedTab: number;
  isDrawerOpen: boolean;
  }

/**
 * Announcement bell animations.
 *
 * These run only while there is an update the user has not opened yet — once
 * the modal is opened the icon settles into a plain static button, so it draws
 * the eye without becoming permanent visual noise.
 */
/** Shake, then hold still for most of the cycle so it never feels frantic. */
const attentionShake = keyframes`
  0%, 62%, 100% { transform: rotate(0deg) scale(1); }
  66%  { transform: rotate(-14deg) scale(1.08); }
  70%  { transform: rotate(12deg)  scale(1.08); }
  74%  { transform: rotate(-9deg)  scale(1.06); }
  78%  { transform: rotate(7deg)   scale(1.04); }
  82%  { transform: rotate(-4deg)  scale(1.02); }
  86%  { transform: rotate(0deg)   scale(1); }
`;

/** Gentle scale + shadow pulse on the pill. */
const breathe = keyframes`
  0%, 100% { transform: scale(1);    box-shadow: 0 2px 10px rgba(60,119,149,0.40); }
  50%      { transform: scale(1.04); box-shadow: 0 5px 18px rgba(60,119,149,0.55); }
`;

/** Gloss sweep across the pill, with a long pause between passes. */
const shimmer = keyframes`
  0%        { left: -60%; }
  55%, 100% { left: 130%; }
`;

/** Paging between announcements: content slides in from the direction of travel. */
const slideFromRight = keyframes`
  from { opacity: 0; transform: translateX(18px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const slideFromLeft = keyframes`
  from { opacity: 0; transform: translateX(-18px); }
  to   { opacity: 1; transform: translateX(0); }
`;

/**
 * Id of the newest announcement the modal has already auto-opened for.
 *
 * Auto-open must fire exactly once per new announcement: on first login, and
 * again whenever a newer one is published. A plain reload must NOT reopen it —
 * only a genuinely newer id does, so the newest id is the natural key.
 */
const AUTO_SHOWN_KEY = "woopsa:lastAutoShownVersionId";

const readAutoShownId = (): string | null => {
  try {
    return localStorage.getItem(AUTO_SHOWN_KEY);
  } catch {
    return null;
  }
};

const writeAutoShownId = (id: string) => {
  try {
    localStorage.setItem(AUTO_SHOWN_KEY, id);
  } catch {
    /* private mode / storage disabled — worst case it opens again next load */
  }
};

/** The app theme primary (#3C7795) and a lighter step for the pill gradient. */
const ACCENT_FROM = "#3C7795";
const ACCENT_TO = "#5A9BBC";
const BRAND_TINT = "rgba(60, 119, 149, 0.12)";

/**
 * Label and icon per announcement type. Colour is intentionally NOT per type —
 * the whole surface uses the single theme primary.
 */
const VERSION_TYPE_META: Record<
  string,
  { label: string; color: string; bg: string; Icon: typeof RocketLaunchIcon }
> = {
  bug_fix: { label: "Bug Fix", color: ACCENT_FROM, bg: BRAND_TINT, Icon: BugReportIcon },
  new_update: { label: "New Update", color: ACCENT_FROM, bg: BRAND_TINT, Icon: RocketLaunchIcon },
  maintenance_announcement: { label: "Maintenance", color: ACCENT_FROM, bg: BRAND_TINT, Icon: BuildCircleIcon },
  new_feature: { label: "New Feature", color: ACCENT_FROM, bg: BRAND_TINT, Icon: AutoAwesomeIcon },
};

const getTypeMeta = (type?: string) =>
  VERSION_TYPE_META[type ?? ""] ?? {
    label: "Update",
    color: ACCENT_FROM,
    bg: BRAND_TINT,
    Icon: RocketLaunchIcon,
  };

const Navbar: React.FC<NavbarProps> = ({ onDrawerToggle, onTabChange, onTabHover, onTabHoverEnd, selectedTab, isDrawerOpen }) => {
  const { t } = useTranslation();
  const mode = useSelector((state: RootState) => state.theme.mode);
  const layout = useSelector((state: RootState) => state.layout.layout);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  // Matches AdminLayout's desktop breakpoint: only there does isDrawerOpen
  // mean "sidebar is collapsed" (on smaller screens it means the temporary
  // drawer is open).
  const isDesktopSidebar = useMediaQuery("(min-width:1200px)");
  // In the vertical layout the logo + collapse toggle live inside the floating
  // sidebar card, so the navbar's left section is only needed on smaller
  // screens (hamburger for the temporary drawer) and in the horizontal layout.
  const showLeftSection = layout !== "vertical" || !isDesktopSidebar;
  const isFloating = layout === "vertical";
  // Floating card height per Figma: 68px max on large screens, stepping down
  // responsively on laptop / tablet widths.
  const barHeight = isFloating ? FLOATING_NAVBAR_HEIGHTS : 65;
  // The floating card has no border, so the inner toolbar fills its full height.
  const toolbarHeight = isFloating ? FLOATING_NAVBAR_HEIGHTS : 65;
  const auth = useSelector((state: RootState) => state.auth);
  const cart = useSelector((state: RootState) => auth?.role === "retailer" ? state.cart : state.salesCart);
  const retailerTradeShowCart = useSelector((state: RootState) => state.retailerTradeShowCart);
  const salesTradeShowCart = useSelector((state: RootState) => state.salesTradeShowCart);
  // Pre-book carts (one pre-book at a time; see preBookCartSlice / salesPreBookCartSlice)
  const preBookCart = useSelector((state: RootState) => state.preBookCart);
  const salesPreBookCart = useSelector((state: RootState) => state.salesPreBookCart);
  const notification = useSelector((state: RootState) => state.notification);
  const navigate = useNavigate();
  const location = useLocation();
  const isOnTradeShowRoute = location.pathname.startsWith("/retailer/trade-show");
  const isOnSalesTradeShowRoute = location.pathname.startsWith("/sales/trade-show");
  // On /retailer/pre-book/:id(/cart) or /sales/pre-book/:id(/cart) the cart icon shows
  // THAT pre-book's cart and opens its cart page (same idea as the return-order cart).
  const preBookRouteMatch = location.pathname.match(/^\/(retailer|sales)\/pre-book\/(\d+)/);
  const isOnPreBookRoute = !!preBookRouteMatch;
  const preBookCartPath = preBookRouteMatch ? `/${preBookRouteMatch[1]}/pre-book/${preBookRouteMatch[2]}/cart` : null;
  const retailerCartCount = isOnTradeShowRoute ? retailerTradeShowCart.count : isOnPreBookRoute ? preBookCart.count : cart.count;
  const salesCartCount = isOnSalesTradeShowRoute ? salesTradeShowCart.count : isOnPreBookRoute ? salesPreBookCart.count : cart.count;
  const displayCartCount = auth?.role === "retailer" ? retailerCartCount : auth?.role === "sales" ? salesCartCount : cart.count;
  const dispatch = useAppDispatch();
  const currentCustomerId = useSelector((state: RootState) => state.auth.isSessionActive?.currentCustomerId);
  // Active trade show id (sent as `tradeShow` query param so the backend can
  // recompute showTradeShow when the retailer/store/customer changes)
  const activeTradeShowId = (auth?.activeTradeShows && auth.activeTradeShows.length > 0)
    ? auth.activeTradeShows[0]?.id ?? null
    : null;
  // Tracks the customer of the most recently initiated setSalesSession request.
  // Multiple setSalesSession calls (e.g. an in-flight request from before a
  // customer switch) resolve into the same global showTradeShow flag and would
  // otherwise race. We set this synchronously at call time via callSalesSession,
  // then ignore any response that is not for the latest requested customer.
  const lastRequestedCustomerRef = useRef<string | null>(null);

  // Wrapper that records the requested customer before firing the API, so stale
  // responses can be detected when they resolve.
  const callSalesSession = (customerId: string) => {
    lastRequestedCustomerRef.current = String(customerId);
    return setSalesSession(customerId, activeTradeShowId);
  };

  // Apply showTradeShow from a setSalesSession response only when it is for the
  // latest requested customer, so a different/stale customer's value can't
  // overwrite the Trade Show tab visibility.
  const applyShowTradeShowForCustomer = (storeDetails: any) => {
    if (!storeDetails || typeof storeDetails.showTradeShow !== "boolean") return;
    const respCustomer = storeDetails.C_Number != null ? String(storeDetails.C_Number) : null;
    if (
      lastRequestedCustomerRef.current != null &&
      respCustomer != null &&
      respCustomer !== lastRequestedCustomerRef.current
    ) {
      return; // stale / different customer - ignore
    }
    dispatch(setShowTradeShow(storeDetails.showTradeShow));
  };

  // State for customer list and loading
  const [customerList, setCustomerList] = useState<any[]>([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [isCustomerListLoaded, setIsCustomerListLoaded] = useState(false);
  const [hasUserManuallySelected, setHasUserManuallySelected] = useState(false);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const [storyModalOpen, setStoryModalOpen] = useState(false);
  const [storiesModalOpen, setStoriesModalOpen] = useState(false);
  const [storiesCount, setStoriesCount] = useState(0);
  // All active announcements; the modal pages through them one at a time.
  const [latestVersions, setLatestVersions] = useState<any[]>([]);
  const [latestVersionIndex, setLatestVersionIndex] = useState(0);
  const [latestVersionModalOpen, setLatestVersionModalOpen] = useState(false);
  // Full-size source of an announcement image opened from its thumbnail.
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  /**
   * Opens an announcement image full size when its thumbnail is clicked.
   *
   * Delegated because the description is injected HTML — there are no React
   * nodes to attach a handler to. Anchors are left alone so a linked image
   * still follows its href.
   */
  // Which way the next announcement should slide in from.
  const [slideForward, setSlideForward] = useState(true);

  /**
   * Paging animation. Re-keying the header and body on the index makes React
   * remount them, which replays the animation on every move.
   */
  const slideSx = {
    animation: `${slideForward ? slideFromRight : slideFromLeft} 0.28s ease-out`,
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  } as const;

  const goToVersion = (nextIndex: number) => {
    if (nextIndex === latestVersionIndex) return;
    setSlideForward(nextIndex > latestVersionIndex);
    setLatestVersionIndex(nextIndex);
  };

  /** Opens the announcements, starting at the newest. */
  const openVersionModal = () => {
    setLatestVersionIndex(0);
    setLatestVersionModalOpen(true);
  };

  /** Closing the announcement also dismisses any open image. */
  const closeVersionModal = () => {
    setLatestVersionModalOpen(false);
    setLightboxSrc(null);
  };

  const handleDescriptionClick = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    if (target?.tagName !== "IMG") return;
    if (target.closest("a")) return;

    const src = (target as HTMLImageElement).currentSrc || (target as HTMLImageElement).src;
    if (src) setLightboxSrc(src);
  };
  // Live outstanding balance for the current customer/store, fetched from the
  // customerBalance API (falls back to storeDetail.LastBalance until it loads).
  const [outstandingBalance, setOutstandingBalance] = useState<number | null>(null);

  // Function to fetch stories count
  const fetchStoriesCount = async () => {
    try {
      const response = await getStories();
      const stories = (response as any)?.data || response || [];
      const activeStories = stories.filter((story: any) => story.isActive);
      setStoriesCount(activeStories.length);
    } catch (error) {
      console.error('Error fetching stories count:', error);
      setStoriesCount(0);
    }
  };

  // Resolve the active trade show id so it can be sent as the `tradeShow`
  // query param when switching customer (sales) or store (retailer).
  // Stored in auth state and persisted, so it survives the store-switch reload.
  useEffect(() => {
    if (!auth?.isAuthenticated) return;
    if (auth?.role !== "sales" && auth?.role !== "retailer") return;
    if (activeTradeShowId != null) return; // already known
    let cancelled = false;
    const fetchActiveTradeShow = auth.role === "sales" ? getSalesTradeShow : getRetailerTradeShow;
    fetchActiveTradeShow()
      .then((res: any) => {
        const id = res?.data?.data?.id ?? res?.data?.id ?? res?.data?.tradeShowId;
        if (!cancelled && id != null) {
          dispatch(setActiveTradeShows([{ id: Number(id) }]));
        }
      })
      .catch(() => {
        // No active trade show / endpoint unavailable - leave id unset
      });
    return () => {
      cancelled = true;
    };
  }, [auth?.isAuthenticated, auth?.role, activeTradeShowId, dispatch]);

  // Fetch cart items and notification count on component mount
  // Only fetch regular retailer cart when NOT on trade show route (avoid conflict with trade show cart)
  useEffect(() => {
    if (auth?.role === "retailer") {
      if (!isOnTradeShowRoute) {
        dispatch(fetchCartItems());
      }
      // Fetch stories count
      fetchStoriesCount();

      // Refresh priceBook flag from API on every mount/refresh so the
      // Sidebar Price Book tab stays correct after a store switch (the
      // switchStore response does not always include priceBook).
      getPriceBook()
        .then((response: any) => {
          const apiPriceBook =
            response?.data?.priceBook ??
            response?.priceBook ??
            response?.data ??
            response;
          dispatch(setPriceBook(apiPriceBook === true));
        })
        .catch((error: any) => {
          console.error("Error fetching priceBook flag:", error);
        });

      // Refresh the allowShowBalance flag from API on every mount/refresh so
      // the Outstanding Balance chip stays correct even if the distributor
      // changed the setting after login (login/verify only carries the
      // login-time value).
      getAllowedShowBalance()
        .then((response: any) => {
          const allowed =
            response?.data?.allowShowBalance ?? response?.allowShowBalance;
          if (typeof allowed === "boolean") {
            dispatch(setAllowShowBalance(allowed));
          }
        })
        .catch((error: any) => {
          console.error("Error fetching allowShowBalance flag:", error);
        });
    } else if (auth?.role === "sales") {
      if (!isOnSalesTradeShowRoute) {
        dispatch(fetchSalesCartItems());
      }
    }
    
    // Fetch notification count for retailer users only
    if (auth?.role === "retailer") {
      dispatch(fetchNotificationCount());
    }
  }, [dispatch, auth?.role, auth?.isAuthenticated, auth?.emailPhone, isOnTradeShowRoute, isOnSalesTradeShowRoute]);

  // Fetch the live outstanding balance for the current customer/store. Runs for
  // retailer and sales (the same C_Number reflects the selected customer for
  // sales), and re-runs whenever the customer/store changes.
  useEffect(() => {
    if (auth?.role !== "retailer" && auth?.role !== "sales") return;
    // Restricted retailers never see the balance chip, so skip the fetch.
    if (auth?.role === "retailer" && auth?.allowShowBalance === false) return;
    const customerId = auth?.storeDetail?.C_Number;
    if (!auth?.isAuthenticated || customerId == null) return;
    let cancelled = false;
    // Reset to null while fetching so, until (and unless) the API succeeds, the
    // chip falls back to the login-time store balance (storeDetail.LastBalance).
    setOutstandingBalance(null);
    // Retailer and sales have separate endpoints that return the same shape:
    // retailer resolves the customer from the token; sales passes the id.
    const balanceRequest =
      auth?.role === "retailer"
        ? getRetailerCustomerBalance()
        : getCustomerBalance(customerId);
    balanceRequest
      .then((res: any) => {
        const balance = res?.data?.data?.Balance;
        if (!cancelled && balance != null) {
          setOutstandingBalance(Number(balance));
        }
      })
      .catch(() => {
        // Silently ignore: on failure the chip keeps showing the previous
        // (login-time) store balance instead of an error.
      });
    return () => {
      cancelled = true;
    };
  }, [auth?.role, auth?.isAuthenticated, auth?.storeDetail?.C_Number, auth?.allowShowBalance]);

  // Refresh the retailer store list + Trade Show tab visibility for the current
  // store. Keyed on activeTradeShowId so it re-runs once the trade show id is
  // resolved, ensuring the `tradeShow` query param is sent and showTradeShow is
  // accurate (e.g. after a store switch reload).
  useEffect(() => {
    if (auth?.role !== "retailer") return;
    const emailPhone = auth?.emailPhone || localStorage.getItem('emailPhone');
    if (!auth?.isAuthenticated || !emailPhone) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailPhone)) return;
    const currentStoreNumber = auth?.storeDetail?.C_Number;
    hasMultipleStore(emailPhone, currentStoreNumber, activeTradeShowId)
      .then((response: any) => {
        if (response?.success && response?.data) {
          dispatch(setMultipleStores({
            hasMultipleStore: response.data.hasMultipleStore,
            stores: response.data.stores || [],
          }));
          // Only trust showTradeShow when we actually sent the tradeShow param.
          // A param-less response can be stale and would wrongly toggle the tab,
          // overwriting the correct value already set at login/store-switch.
          if (activeTradeShowId != null && typeof response.data.showTradeShow === 'boolean') {
            dispatch(setShowTradeShow(response.data.showTradeShow));
          }
          // NOTE: do NOT dispatch setActiveTradeShows here. The dedicated
          // id-resolve effect (getRetailerTradeShow) owns activeTradeShows.
          // Writing it from here too made the two effects fight and re-trigger
          // each other on every render (infinite request loop).
        } else {
          // If response is not successful, clear the stores
          dispatch(setMultipleStores({ hasMultipleStore: false, stores: [] }));
        }
      })
      .catch((error) => {
        console.error('Error fetching multiple stores:', error);
        // On error, don't clear stores - keep existing state
      });
  }, [dispatch, auth?.role, auth?.isAuthenticated, auth?.emailPhone, auth?.storeDetail?.C_Number, activeTradeShowId]);

  // Fetch version info for distributor users. The API may return a single
  // version object or a list of them, so both shapes are normalised to an
  // array and the modal pages through every active announcement.
  useEffect(() => {
    if (auth?.role === "distributor") {
      getLatestVersion()
        .then((response: any) => {
          const payload = response?.success ? response?.data : null;
          const versions = (
            Array.isArray(payload) ? payload : payload ? [payload] : []
          ).filter((version: any) => version?.isActive && version?.showDistributor);
          setLatestVersions(versions);
          setLatestVersionIndex(0);

          // Open by itself the first time this announcement is seen — covers
          // "none -> some" on first login and "4 -> 5" when a newer one is
          // published. Recording the id immediately means a hard refresh will
          // not reopen it; after this the pill has to be clicked.
          const newestId = versions[0]?.id;
          if (newestId !== undefined && String(newestId) !== readAutoShownId()) {
            writeAutoShownId(String(newestId));
            setLatestVersionModalOpen(true);
          }
        })
        .catch((error: any) => {
          console.error("Error fetching latest version info:", error);
          setLatestVersions([]);
          setLatestVersionIndex(0);
        });
    } else {
      setLatestVersions([]);
      setLatestVersionIndex(0);
    }
  }, [auth?.role]);

  // Fetch customer list for sales users
  useEffect(() => {
    if (auth?.role === "sales") {
      setCustomerLoading(true);
      getCustomerList().then((res: any) => {
        const customers = res?.data?.data || [];
        setCustomerList(customers);
        setIsCustomerListLoaded(true);
        
        // If currentCustomerId exists, find and select that customer
        if (currentCustomerId) {
          const currentCustomer = customers.find((customer: any) => customer.C_Number.toString() === currentCustomerId.toString());
          if (currentCustomer) {
            dispatch(setSelectedCustomer(currentCustomer));
            
            // Update store details for the current customer
            callSalesSession(currentCustomer.C_Number.toString()).then((sessionResponse: any) => {
              if (sessionResponse && typeof sessionResponse === 'object' && 'data' in sessionResponse) {
                const responseData = sessionResponse as any;
                const sessionData = responseData.data?.data ?? responseData.data;
                if (sessionData) {
                  const storeDetails = sessionData;
                  dispatch({
                    type: 'auth/updateStoreDetails',
                    payload: {
                      C_CoName: storeDetails.C_CoName,
                      C_Name: storeDetails.C_Name,
                      C_Number: storeDetails.C_Number,
                      C_Address: storeDetails.C_Address,
                      C_City: storeDetails.C_City,
                      C_State: storeDetails.C_State,
                      C_Phone: storeDetails.C_Phone,
                      LastBalance: storeDetails.LastBalance,
                      C_OrderDay: storeDetails.C_OrderDay,
                      Routes: storeDetails.Routes,
                      salesRep: storeDetails.salesRep,
                      C_Zip: storeDetails.C_Zip,
                      Jurisdiction_State: storeDetails.Jurisdiction_State,
                    }
                  });
                  applyShowTradeShowForCustomer(storeDetails);
                }
              }
            }).catch((error: any) => {
              console.error('Error updating store details for initial customer:', error);
            });
          }
        }
        
        setCustomerLoading(false);
      }).catch((err: any) => {
        console.log(err);
        setCustomerLoading(false);
        setIsCustomerListLoaded(true);
      });
    }
  }, [auth?.role, dispatch, currentCustomerId, activeTradeShowId]);

  // Handle customer selection when currentCustomerId changes after customer list is loaded
  useEffect(() => {
    if (auth?.role === "sales" && currentCustomerId && isCustomerListLoaded && customerList.length > 0 && !hasUserManuallySelected) {
      const currentCustomer = customerList.find((customer: any) => customer.C_Number.toString() === currentCustomerId.toString());
      if (currentCustomer && (!auth.selectedCustomer || auth.selectedCustomer.C_Number.toString() !== currentCustomerId.toString())) {
        dispatch(setSelectedCustomer(currentCustomer));
        
        // Update store details for the auto-selected customer
        setSalesSession(currentCustomer.C_Number.toString(), activeTradeShowId).then((sessionResponse: any) => {
          if (sessionResponse && typeof sessionResponse === 'object' && 'data' in sessionResponse) {
            const responseData = sessionResponse as any;
            const sessionData = responseData.data?.data ?? responseData.data;
            if (sessionData) {
              const storeDetails = sessionData;
              dispatch({
                type: 'auth/updateStoreDetails',
                payload: {
                  C_CoName: storeDetails.C_CoName,
                  C_Name: storeDetails.C_Name,
                  C_Number: storeDetails.C_Number,
                  C_Address: storeDetails.C_Address,
                  C_City: storeDetails.C_City,
                  C_State: storeDetails.C_State,
                  C_Phone: storeDetails.C_Phone,
                  LastBalance: storeDetails.LastBalance,
                  C_OrderDay: storeDetails.C_OrderDay,
                  Routes: storeDetails.Routes,
                  salesRep: storeDetails.salesRep,
                  C_Zip: storeDetails.C_Zip,
                  Jurisdiction_State: storeDetails.Jurisdiction_State,
                }
              });
              applyShowTradeShowForCustomer(storeDetails);
            }
          }
        }).catch((error: any) => {
          console.error('Error updating store details for auto-selected customer:', error);
        });
      }
    }
  }, [currentCustomerId, isCustomerListLoaded, customerList, auth?.role, auth.selectedCustomer, hasUserManuallySelected, dispatch]);

  // Reset manual selection flag when currentCustomerId changes (new login)
  useEffect(() => {
    if (currentCustomerId) {
      setHasUserManuallySelected(false);
    }
  }, [currentCustomerId]);

  // Handle store selection for retailer
  const handleStoreSelect = async (selectedOption: any) => {
    if (selectedOption && auth?.role === "retailer") {
      try {
        const selectedStore = auth.stores?.find(store => store.C_Number.toString() === selectedOption.value);
        if (!selectedStore) {
          console.error('Selected store not found in stores list');
          return;
        }
        
        // Check if user is trying to switch to the same store
        const currentStoreNumber = auth.storeDetail?.C_Number;
        const selectedStoreNumber = selectedStore.C_Number;
        
        console.log('Store switch attempt:', {
          currentStoreNumber,
          selectedStoreNumber,
          currentStoreString: currentStoreNumber?.toString(),
          selectedStoreString: selectedStoreNumber.toString()
        });
        
        // Compare as numbers or strings to handle type mismatches
        if (currentStoreNumber && (
          currentStoreNumber.toString() === selectedStoreNumber.toString() ||
          Number(currentStoreNumber) === Number(selectedStoreNumber)
        )) {
          // Already on this store, no need to switch
          console.log('Already on selected store, skipping switch');
          return;
        }
        
        // Save emailPhone before logout (it will be cleared)
        const savedEmailPhone = auth.emailPhone || localStorage.getItem('emailPhone');
        
        if (!savedEmailPhone) {
          console.error('EmailPhone not found, cannot switch store');
          return;
        }
        
        console.log('Calling switchStore API with retailerId:', selectedStore.C_Number.toString());
        
        // Call switchStore API
        const switchResponse: any = await switchStore(selectedStore.C_Number.toString());
        
        console.log('SwitchStore API response:', switchResponse);
        
        // Check if switch was successful
        if (!switchResponse?.success) {
          console.error('Switch store API failed - success is false:', switchResponse);
          return;
        }
        
        if (!switchResponse?.data) {
          console.error('Switch store API failed - no data in response:', switchResponse);
          return;
        }
        
        // Call logout function
        try {
          await authLogout();
        } catch (error) {
          console.log('Logout error (non-critical):', error);
        }
        
        // Clear Redux state
        dispatch(logout());
        dispatch(clearCart());
        dispatch(clearSalesCart());
        dispatch(clearDashboardData());
        dispatch(clearSalesDashboardData());
        
        // Handle switchStore response like verifyOtp (only for retailer)
        // The response structure should be: { success: true, data: { token, role, wareHouseDetail, storeDetail, logo } }
        const authData = switchResponse.data;
        console.log('Setting auth from switchStore response:', { 
          hasToken: !!authData.token, 
          hasStoreDetail: !!authData.storeDetail,
          storeDetailCNumber: authData.storeDetail?.C_Number,
          authDataKeys: Object.keys(authData || {})
        });
        
        // Validate required fields
        if (!authData.token) {
          console.error('SwitchStore response missing token');
          return;
        }
        
        if (!authData.storeDetail) {
          console.error('SwitchStore response missing storeDetail');
          return;
        }
        
        // Set auth state from switchStore response (same as verifyOtp) - sets token and auth data once
        dispatch(setAuthFromSwitchStore({ authData, emailPhone: savedEmailPhone }));
        
        // Small delay to ensure localStorage and Redux state are updated before the full reload.
        // Land on the dashboard (like the sales customer switch) instead of reloading the
        // current page, which may belong to the previous store (cart, pre-book, order...).
        setTimeout(() => {
          console.log('Redirecting to dashboard after store switch...');
          window.location.assign('/retailer/dashboard');
        }, 100);
      } catch (error) {
        console.error('Error switching store:', error);
      }
    }
  };

  // Handle customer selection
  const handleCustomerSelect = async (selectedOption: any) => {
    if (selectedOption) {
      try {
        // Find the customer object from the list
        const selectedCustomer = customerList?.find(customer => customer.C_Number.toString() === selectedOption.value);
        
        if (selectedCustomer) {
          // Set flag to indicate user has manually selected a customer
          setHasUserManuallySelected(true);
          
          // Call setSalesSession API
          const sessionResponse = await callSalesSession(selectedCustomer?.C_Number.toString());
          
          // Update session state in Redux
          dispatch(updateSessionCustomer(selectedCustomer.C_Number.toString()));
          
          // Store selected customer in Redux
          dispatch(setSelectedCustomer(selectedCustomer));
          
          // Update store details with the response from setSalesSession
          if (sessionResponse && typeof sessionResponse === 'object' && 'data' in sessionResponse) {
            const responseData = sessionResponse as any;
            const sessionData = responseData.data?.data ?? responseData.data;
            if (sessionData) {
              const storeDetails = sessionData;
              dispatch({
                type: 'auth/updateStoreDetails',
                payload: {
                  C_CoName: storeDetails.C_CoName,
                  C_Name: storeDetails.C_Name,
                  C_Number: storeDetails.C_Number,
                  C_Address: storeDetails.C_Address,
                  C_City: storeDetails.C_City,
                  C_State: storeDetails.C_State,
                  C_Phone: storeDetails.C_Phone,
                  LastBalance: Number(storeDetails.LastBalance),
                  C_OrderDay: storeDetails.C_OrderDay,
                  Routes: storeDetails.Routes,
                  salesRep: storeDetails.salesRep,
                  C_Zip: storeDetails.C_Zip,
                  Jurisdiction_State: storeDetails.Jurisdiction_State,
                }
              });
              applyShowTradeShowForCustomer(storeDetails);
            }
          }
          
          // When sales user changes retailer, redirect to dashboard
          if (auth?.role === "sales") {
            navigate("/sales/dashboard");
          }
        }else{

          callSalesSession(selectedCustomer .C_Number.toString()).then((sessionResponse: any) => {

            if (sessionResponse && typeof sessionResponse === 'object' && 'data' in sessionResponse) {

              const responseData = sessionResponse as any;

              const sessionData = responseData.data?.data ?? responseData.data;
              if (sessionData) {

                const storeDetails = sessionData;

                dispatch({

                  type: 'auth/updateStoreDetails',

                  payload: {

                    C_CoName: storeDetails.C_CoName,

                    C_Name: storeDetails.C_Name,

                    C_Number: storeDetails.C_Number,

                    C_Address: storeDetails.C_Address,

                    C_City: storeDetails.C_City,

                    C_State: storeDetails.C_State,

                    C_Phone: storeDetails.C_Phone,

                    LastBalance: storeDetails.LastBalance,

                    C_OrderDay: storeDetails.C_OrderDay, 

                    Routes: storeDetails.Routes,

                    salesRep: storeDetails.salesRep,

                    C_Zip: storeDetails.C_Zip,

                    Jurisdiction_State: storeDetails.Jurisdiction_State,

                  }

                });
                applyShowTradeShowForCustomer(storeDetails);
              }

            }

          }).catch((error: any) => {

            console.error('Error updating store details for initial customer:', error);

          });


        }
      } catch (error) {
        console.error('Error setting sales session:', error);
      }
    }
  };

  // Convert customer list to dropdown options
  const customerOptions = customerList?.map(customer => ({
    label: customer.C_Name,
    value: customer.C_Number.toString()
  }));

  // Convert store list to dropdown options
  const storeOptions = auth.stores?.map(store => ({
    label: store.C_CoName,
    value: store.C_Number.toString()
  })) || [];

  // Get selected store option
  // Use selectedStore if available, otherwise find current store from storeDetail
  const selectedStoreOption = auth.selectedStore ? {
    label: auth.selectedStore.C_CoName,
    value: auth.selectedStore.C_Number.toString()
  } : (auth.storeDetail?.C_Number && auth.stores && auth.stores.length > 0) ? (() => {
    const currentStore = auth.stores.find(store => 
      store.C_Number.toString() === auth.storeDetail?.C_Number?.toString()
    );
    return currentStore ? {
      label: currentStore.C_CoName,
      value: currentStore.C_Number.toString()
    } : undefined;
  })() : undefined;

  // // Custom filter function for Autocomplete to search from any position
  // const filterOptions = (options: any[], { inputValue }: { inputValue: string }) => {
  //   if (!inputValue || inputValue.trim() === '') return options;
    
  //   const searchTerm = inputValue.toLowerCase().trim();
  //   return options.filter(option => 
  //     option.label.toLowerCase().includes(searchTerm)
  //   );
  // };

  // Get selected customer option
  const selectedCustomerOption = auth.selectedCustomer ? {
    label: auth.selectedCustomer.C_Name,
    value: auth.selectedCustomer.C_Number.toString()
  } : (currentCustomerId && isCustomerListLoaded && customerList.length > 0) ? customerOptions?.find(option => option.value === currentCustomerId.toString()) : undefined;

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Static tabs configuration. Tab icons render inside a padded rounded
  // square with a light-blue background (see the icon chip in the Tabs below).
  const storeTabIcon = (
    <StorefrontOutlinedIcon sx={{ fontSize: 18, color: "primary.main" }} />
  );
  const distributorTabIcon = (
    <Box
      component="img"
      src={distributorIcon}
      alt="Distributor"
      sx={{ width: 18, height: 18 }}
    />
  );
  const staticTabs = [
    { label: "Store Details", labelKey: "navbar.storeDetails", value: 0, icon: storeTabIcon },
    { label: "Distributor Details", labelKey: "navbar.distributorDetails", value: 1, icon: distributorTabIcon }
  ];
  const staticTabsForDistributor = [
    { label: "Distributor Details", labelKey: "navbar.distributorDetails", value: 0, icon: distributorTabIcon }
  ];

  const staticTabsForSales = [
    { label: "Store Details", labelKey: "navbar.storeDetails", value: 0, icon: storeTabIcon },
    { label: "Distributor Details", labelKey: "navbar.distributorDetails", value: 1, icon: distributorTabIcon }
  ];

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (value?: number) => {
    setAnchorEl(null);
    if (typeof value === 'number') {
      onTabChange?.(value);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    onTabChange?.(newValue);
  };

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: theme.palette.background.paper,
        color: "text.primary",
        boxShadow: "none",
        // Floating rounded card in the vertical layout (matches the sidebar
        // card); the horizontal layout keeps the classic full-width bar.
        ...(isFloating && {
          // Figma: 8px radius, 68px max height, NO border and no shadow
          // (plain white card). While the details popup is open
          // (selectedTab >= 0) the bottom corners square off so the popup
          // attaches seamlessly below.
          borderRadius: selectedTab >= 0 ? "8px 8px 0 0" : "8px",
          // Dark mode: the card sits on a same-black page, so a hairline border
          // outlines it (light mode stays borderless per Figma). The bottom edge
          // opens while the details popup is attached below.
          border: mode === "dark" ? "1px solid #2A2A33" : "none",
          borderBottom: mode === "dark" && selectedTab >= 0 ? "none" : undefined,
          overflow: "hidden",
          height: barHeight,
        }),
      }}
      elevation={isFloating ? 0 : 1}
    >
      <Toolbar
        sx={{
          justifyContent: "space-between",
          // pl: 0.3,
          minHeight: toolbarHeight,
          height: isFloating ? toolbarHeight : "auto",
          // Figma: 16px inner padding inside the floating card
          ...(isFloating && { px: { xs: 2, sm: 2 } }),
          gap: isMobile ? 0 : 2,
          position: "relative",
          ...(!isFloating && {
            "&:after": {
              content: '""',
              position: "absolute",
              left: 0,
              bottom: 0,
              width: "100%",
              height: "1px",
              backgroundColor: mode === "dark" ? "#444" : "rgba(0, 0, 0, 0.12)",
              zIndex: 1,
            },
          }),
        }}
        style={{ backgroundColor: theme.palette.background.paper }}
      >
        {/* Left Section — hidden on desktop vertical layout (logo + collapse
            toggle live inside the sidebar card there) */}
        {showLeftSection && (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {!isTablet && (
              <Box
                component="img"
                src={logo}
                alt="Woopsa"
                sx={{
                  height: 30,
                }}
              />
            )}

            {layout === "vertical" && (
              <IconButton
                color="primary"
                edge="start"
                onClick={onDrawerToggle}
                sx={{
                  border: "1px solid",
                  borderRadius: "8px",
                  padding: 0.5,
                  height: 32,
                  width: 32,
                  ml: isTablet ? 0.3 : 0,
                }}
              >
                {isDrawerOpen ?   <MoreVertIcon sx={{ fontSize: 20 }} /> :<MenuIcon sx={{ fontSize: 20 }} />}
              </IconButton>
            )}
          </Box>
        )}

        {/* Tabs/Menu Section */}
        <Box sx={{ position: "relative", flex: 1, ml: 1 }}>
          {isMobile ? (
            // Mobile Menu View
            <Box display="flex" alignItems="center" justifyContent="flex-end" width="100%" sx={{ display: { xs: "flex", md: "none" } }}>
              <IconButton
                onClick={handleClick}
                sx={{
                  color: "primary.main",
                }}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => handleClose()}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    minWidth: 180,
                    mt: 1,
                  }
                }}
              >
                {(auth?.role === "distributor" ? staticTabsForDistributor : auth?.role === "sales" ? staticTabsForSales : staticTabs).map((tab) => (
                  <MenuItem 
                    key={tab.value}
                    onClick={() => handleClose(tab.value)}
                    selected={selectedTab === tab.value}
                    sx={{
                      fontSize: "14px",
                      py: 1,
                    }}
                  >
                    {t(tab.labelKey)}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          ) : (
            // Desktop/Tablet Tab View
            <Tabs
              value={selectedTab >= 0 ? selectedTab : false}
              onChange={handleTabChange}
              variant="standard"
              sx={{
                minHeight: toolbarHeight,
                zIndex: 1200,
                "& .MuiTab-root": {
                  textTransform: "none",
                  // Figma: 16px / weight 500 label, scaling down with the
                  // responsive bar height
                  fontSize: isFloating
                    ? { xs: "14px", md: "15px", lg: "16px" }
                    : "14px",
                  fontWeight: isFloating ? 500 : 400,
                  minHeight: toolbarHeight,
                  // Figma: chip sits 20px from the card edge (16px toolbar
                  // padding + 4px tab padding)
                  px: isFloating ? 0.5 : { xs: 2, md: 3 },
                  minWidth: { xs: "auto", md: 100 },
                  color: "text.secondary",
                  "&.Mui-selected": {
                    // Floating (Figma) keeps the quiet gray label even while
                    // the details popup is open
                    color: isFloating ? "text.secondary" : "primary.main",
                    fontWeight: 500,
                  },
                },
                "& .MuiTabs-indicator": {
                  // The floating rounded navbar drops the underline indicator;
                  // selection is shown by the highlighted icon chip + label.
                  display: isFloating ? "none" : "block",
                  height: 3,
                  borderRadius: "3px 3px 0 0",
                  zIndex: 1,
                },
              }}
            >
              {(auth?.role === "distributor" ? staticTabsForDistributor : auth?.role === "sales" && auth?.module?.some((moduleItem: any) => moduleItem.module === "Dashboard" && moduleItem?.view === true) ? staticTabsForSales : auth?.role === "retailer" ? staticTabs : []).map((tab) => (
                <Tab
                  key={tab.value}
                  onMouseEnter={() => onTabHover?.(tab.value)}
                  onMouseLeave={() => onTabHoverEnd?.()}
                  icon={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        // Figma "Distributor-Icon": 38×38 (18px icon + 10px
                        // padding), 4px radius, #F6FBFF fill, 10px gap to the
                        // label; scales down with the bar height
                        width: { xs: 32, md: 34, lg: 38 },
                        height: { xs: 32, md: 34, lg: 38 },
                        borderRadius: "4px",
                        bgcolor:
                          mode === "dark"
                            ? "rgba(60, 119, 149, 0.25)"
                            : "#F6FBFF",
                        flexShrink: 0,
                        mr: 1.25,
                      }}
                    >
                      {tab.icon}
                    </Box>
                  }
                  iconPosition="start"
                  label={t(tab.labelKey)}
                  sx={{
                    "&.Mui-selected": {
                      fontWeight: 500,
                      zIndex: 1200,
                    },
                  }}
                />
              ))}
            </Tabs>
          )}
        </Box>

        {/* Right Section */}
        <Box
          display="flex"
          alignItems="center"
          gap={isMobile ? 0 : 1}
          sx={{
            ml: "auto",
            // "& > *": {
            //   display: { xs: "none", sm: "flex" },
            // },
            // "& > :last-child": {
            //   display: "flex", // Always show AvatarMenu
            // },
          }}
        >
          {/* Balance + Days to Order boxes */}
          {(
            // Show for:
            // - Not distributor: show.
            // - For "sales": only show if Dashboard module present with view=true.
            // - Never for distributor.
            auth?.role !== "distributor" && (
              auth?.role !== "sales" ||
              (
                Array.isArray(auth?.module) &&
                auth?.module.some(
                  (moduleItem: any) =>
                    moduleItem.module === "Dashboard" && moduleItem?.view === true
                )
              )
            )
          ) && (
            <>
              {/* Balance Box — for "retailer" only if allowShowBalance is not false
                  (from /auth/verify + /retailer/getAllowedShowBalance). This gate is
                  deliberately NOT around the Days to Order box below: hiding the
                  balance must never hide the order day. */}
              {(auth?.role !== "retailer" || auth?.allowShowBalance !== false) && (
              <Box
                sx={{
                  height: 40,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: "18px",
                  pl: 0.5,
                  pr: 2,
                  bgcolor: "background.paper",
                  display: { xs: "none", lg: "flex" },
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(17, 197, 17, 0.2)"
                          : "rgba(17, 197, 17, 0.2)",
                      borderRadius: "50%",
                      padding: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <BalanceIcon />
                  </Box>
                  <Typography fontSize={"14px"} fontWeight={400} color="text.secondary">
                    {t("navbar.outstandingBalance")} <span style={{ fontWeight: 600, color: "primary.secondary" }}>${ Number(outstandingBalance ?? auth?.storeDetail?.LastBalance ?? 0).toFixed(2)}</span>
                  </Typography>
                </Box>
              </Box>
              )}
              {/* Days to Order Box */}
              <Box
                sx={{
                  height: 40,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: "20px",
                  pl: 0.5,
                  pr: 2,
                  bgcolor: "background.paper",
                  display: { xs: "none", lg: "flex" },
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {(() => {
                    // 0 = N/A, 8 = Other, 1=Monday ... 7=Sunday
                    const orderDayRaw = auth?.storeDetail?.C_OrderDay;
                    const orderDay = typeof orderDayRaw === 'number'
                      ? orderDayRaw
                      : parseInt(orderDayRaw || '0', 10);

                    let daysLeft: number | string = 'N/A';
                    let isOrderDayToday = false;
                    if (orderDay === 0) {
                      daysLeft = 'NA';
                    } else if (orderDay === 8) {
                      daysLeft = 'OT';
                    } else if (orderDay >= 1 && orderDay <= 7) {
                      const today = new Date();
                      const todayDay = today.getDay(); // 0=Sunday, ..., 6=Saturday
                      const ourTodayDay = todayDay === 0 ? 7 : todayDay;
                      const diff = (orderDay - ourTodayDay + 7) % 7;
                      if (diff === 0) {
                        isOrderDayToday = true;
                      } else {
                        daysLeft = diff;
                      }
                    }

                    // Determine color based on daysLeft
                    let borderColor = "#FFCD1B"; // default yellow
                    let bgColor = "transparent";
                    if (typeof daysLeft === "number") {
                      if (daysLeft >= 5 && daysLeft <= 7) {
                        borderColor = "#11C511"; // green
                        bgColor = "rgba(17, 197, 17, 0.15)";
                      } else if (daysLeft >= 3 && daysLeft <= 4) {
                        borderColor = "#FFCD1B"; // yellow
                        bgColor = "rgba(255, 205, 27, 0.15)";
                      } else if (daysLeft === 2) {
                        borderColor = "#FF9800"; // orange
                        bgColor = "rgba(255, 152, 0, 0.15)";
                      } else if (daysLeft <= 1) {
                        borderColor = "#FF3333"; // red
                        bgColor = "rgba(255, 51, 51, 0.15)";
                      }
                    } else if (isOrderDayToday) {
                      borderColor = "#11C511";
                      bgColor = "rgba(17, 197, 17, 0.15)";
                    }

                    // Map orderDay to day name
                    const dayNames = {
                      1: t("navbar.monday"),
                      2: t("navbar.tuesday"),
                      3: t("navbar.wednesday"),
                      4: t("navbar.thursday"),
                      5: t("navbar.friday"),
                      6: t("navbar.saturday"),
                      7: t("navbar.sunday")
                    };
                    let orderDayText = "";
                    if (orderDay >= 1 && orderDay <= 7) {
                      orderDayText = dayNames[orderDay as keyof typeof dayNames];
                    } else if (orderDay === 8) {
                      orderDayText = t("navbar.other");
                    } else {
                      orderDayText = "";
                    }

                    return (
                      <>
                        {!isOrderDayToday && <Box
                          sx={{
                            border: `2px solid ${borderColor}`,
                            backgroundColor: bgColor,
                            borderRadius: "50%",
                            minWidth: 30,
                            minHeight: 30,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            color: "text.secondary",
                            lineHeight: 1,
                          }}
                        >
                            {daysLeft}
                        </Box>}
                        <Typography fontSize={"14px"} fontWeight={400} color="text.secondary" ml={isOrderDayToday ? 1 : 0}>
                          {isOrderDayToday
                            ? t("navbar.todayIsYourOrderDay")
                            : orderDay === 0
                              ? t("navbar.noOrderDaySet")
                              : orderDay === 8
                                ? t("navbar.orderDayOther")
                                : daysLeft !== 'N/A'
                                  ? orderDayText
                                    ? t("navbar.daysToGoToPlaceTheOrderWithDay", { day: orderDayText })
                                    : t("navbar.daysToGoToPlaceTheOrder")
                                  : t("navbar.daysToGoToPlaceTheOrder")
                          }
                        </Typography>
                      </>
                    );
                  })()}
                </Box>
              </Box>
            </>
          )}
          {auth?.role === "sales" && auth?.module?.some((moduleItem: any) => moduleItem.module === "Dashboard" && moduleItem?.view === true) && (
            <Box sx={{ minWidth: { xs: 150, md: 200 } }}>
              {isCustomerListLoaded ? (
                <CustomSearchDropdown
                options={customerOptions}
                value={selectedCustomerOption || null}
                onChange={handleCustomerSelect}
                placeholder={t("navbar.selectRetailer")}
                loading={customerLoading}
              clearOnFocus
              />
              ) : (
                <TextField
                  placeholder={t("navbar.loading")}
                  size="small"
                  disabled
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "18px",
                      height: 35,
                      fontSize: "12px",
                      backgroundColor: (theme: Theme) => theme.palette.primary.main,
                      color: "#fff",
                      "& input": {
                        color: "#fff",
                        "&::placeholder": {
                          color: "#fff",
                          opacity: 0.7
                        }
                      },
                      "& fieldset": {
                        border: "1px solid",
                        borderColor: (theme: Theme) => theme.palette.primary.main,
                      }
                    }
                  }}
                />
              )}
            </Box>
          )}
          {/* Store Dropdown for Retailer with Multiple Stores */}
          {auth?.role === "retailer" && auth?.hasMultipleStore && storeOptions.length > 0 && (
            <Box sx={{ minWidth: { xs: 150, md: 200 } }}>
              <CustomSearchDropdown
                options={storeOptions}
                value={selectedStoreOption || null}
                onChange={handleStoreSelect}
                placeholder={t("navbar.selectStore")}
                loading={false}
              />
            </Box>
          )}
          {/* Sales chat notifications — a separate system from the retailer bell below. */}
          {auth?.role === "sales" && <SalesNotificationMenu />}

          {/* Notification Icon */}
          {auth?.role === "retailer" && (
            <IconButton
              color="primary"
              onClick={handleNotificationClick}
              sx={{ 
                position: 'relative',
                '&:hover': {
                  backgroundColor: 'rgba(60, 119, 149, 0.08)',
                },
              }}
            >
              <Badge
                badgeContent={notification?.unreadCount || 0}
                color="error"
                max={99}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.75rem',
                    height: '18px',
                    minWidth: '18px',
                    borderRadius: '9px',
                    fontWeight: 600,
                  },
                }}
              >
                <NotificationsNoneIcon />
              </Badge>
            </IconButton>
          )}

          {/* Stories Icon - Only for Retailer */}
          {auth?.role === "retailer" && (
            <IconButton 
              color="primary" 
              onClick={() => setStoriesModalOpen(true)}
              sx={{ 
                position: 'relative',
                '&:hover': {
                  backgroundColor: 'rgba(60, 119, 149, 0.08)',
                },
              }}
            >
              <Badge
                badgeContent={storiesCount}
                color="error"
                max={99}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.75rem',
                    height: '18px',
                    minWidth: '18px',
                    borderRadius: '9px',
                    fontWeight: 600,
                    display: storiesCount > 0 ? 'flex' : 'none',
                  },
                }}
              >
                <img src={ViewStory} alt={t("navbar.viewStories")} style={{ width: 24, height: 24 }} />
              </Badge>
            </IconButton>
          )}

          {/* Add Story Button - Only for Distributor */}
        

          {/* Cart Icon */}
          {auth?.role !== "distributor" && (() => {
            // For sales users, check if they have the "Orders" module with view permission
            if (auth?.role === "sales") {
              const hasOrderModule = auth?.module?.some(
                (moduleItem: any) => moduleItem.module === "Orders" && moduleItem?.view === true
              );
              // Hide cart if sales user doesn't have Order module
              if (!hasOrderModule) {
                return null;
              }
            }
            
            // Check if user is already on a cart page (regular cart or trade show cart)
            const isOnPreBookCartPage = isOnPreBookRoute && location.pathname.endsWith('/cart');
            const isOnCartPage = isOnPreBookCartPage || (auth?.role === "retailer"
              ? location.pathname === "/retailer/cart" || location.pathname.includes("/retailer/trade-show/cart")
              : location.pathname.includes('/sales/cart') || location.pathname.includes('/sales/return-cart') || location.pathname.includes('/sales/trade-show/cart'));

            return (
              <IconButton
                color="primary"
                onClick={() => {
                  if (preBookCartPath) {
                    // The pre-book product page listens for this and runs the same
                    // checks as its Continue button (min order amount, item limits)
                    // before navigating. If nothing is listening, just open the cart.
                    const ev = new CustomEvent("prebook:cart-click", { cancelable: true });
                    window.dispatchEvent(ev);
                    if (!ev.defaultPrevented) navigate(preBookCartPath);
                    return;
                  }
                  if (auth?.role === "retailer" && isOnTradeShowRoute) {
                    navigate("/retailer/trade-show/cart");
                    return;
                  }
                  if (auth?.role === "sales" && isOnSalesTradeShowRoute) {
                    navigate("/sales/trade-show/cart");
                    return;
                  }

                  // Directly navigate to the appropriate cart page without extra validation.
                  // Validation is already handled in the respective Order/Cart flows.
                  if (auth?.role === "retailer") {
                    navigate('/retailer/cart');
                  } else if (auth?.role === "sales") {
                    const isOnReturnOrderPage = location.pathname.includes('/sales/return-order');
                    navigate(isOnReturnOrderPage ? '/sales/return-cart' : '/sales/cart');
                  } else {
                    navigate('/sales/cart');
                  }
                }}
                disabled={isOnCartPage}
                sx={{
                  opacity: isOnCartPage ? 0.5 : 1,
                  cursor: isOnCartPage ? 'not-allowed' : 'pointer',
                }}
              >
                <CartIcon count={displayCartCount} />
              </IconButton>
            );
          })()}
          {/* Latest Version pill - Only for Distributor and only when data exists */}
          {auth?.role === "distributor" && latestVersions.length > 0 && (
            <Tooltip
              title={t("navbar.newVersionAvailable")}
              arrow
            >
              {/* Always animated while any update is live: shimmer, breathe and
                  icon shake run on different periods so they never sync into a
                  mechanical loop. */}
              <ButtonBase
                  onClick={openVersionModal}
                  aria-label={t("navbar.newVersionAvailable")}
                  sx={{
                    position: "relative",
                    overflow: "hidden",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    height: 32,
                    px: { xs: 1.1, sm: 1.6 },
                    borderRadius: 999,
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 12.5,
                    letterSpacing: 0.2,
                    whiteSpace: "nowrap",
                    background: `linear-gradient(135deg, ${ACCENT_FROM} 0%, ${ACCENT_TO} 100%)`,
                    animation: `${breathe} 2.6s ease-in-out infinite`,
                    transition: "transform 0.2s ease",
                    "&:hover": { transform: "translateY(-1px) scale(1.03)" },
                    // Gloss sweep, repeating with a long pause between passes.
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      width: "45%",
                      background:
                        "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)",
                      transform: "skewX(-20deg)",
                      animation: `${shimmer} 3.4s ease-in-out infinite`,
                      pointerEvents: "none",
                    },
                    "@media (prefers-reduced-motion: reduce)": {
                      animation: "none",
                      "&::after": { animation: "none", opacity: 0 },
                    },
                  }}
                >
                  <NewReleasesIcon
                    sx={{
                      fontSize: 17,
                      animation: `${attentionShake} 3.2s ease-in-out infinite`,
                      transformOrigin: "50% 50%",
                      "@media (prefers-reduced-motion: reduce)": {
                        animation: "none",
                      },
                    }}
                  />
                  {/* Label is hidden on the tightest widths so the navbar never
                      overflows, but the pill itself still stands out. */}
                  <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                    {t("navbar.whatsNew", { defaultValue: "What's New" })}
                  </Box>
                  {latestVersions.length > 1 && (
                    <Box
                      component="span"
                      sx={{
                        minWidth: 17,
                        height: 17,
                        px: 0.4,
                        borderRadius: 999,
                        bgcolor: "rgba(255,255,255,0.9)",
                        color: ACCENT_FROM,
                        fontSize: 10.5,
                        fontWeight: 800,
                        lineHeight: "17px",
                        textAlign: "center",
                      }}
                    >
                      {latestVersions.length}
                    </Box>
                  )}
                </ButtonBase>
            </Tooltip>
          )}

          {/* Language switcher (retailer only; component self-gates by role) */}
          <LanguageSelector />

          <AvatarMenu />
        </Box>
      </Toolbar>

      {/* Notification Drawer */}
      {auth?.role === "retailer" && (
        <NotificationDrawer
          open={Boolean(notificationAnchorEl)}
          onClose={handleNotificationClose}
        />
      )}

      {/* Story Modal */}
      {auth?.role === "distributor" && (
        <Story
          open={storyModalOpen}
          onClose={() => setStoryModalOpen(false)}
        />
      )}

      {/* Stories Modal */}
      {auth?.role === "retailer" && (
        <Stories
          open={storiesModalOpen}
          onClose={() => {
            setStoriesModalOpen(false);
            fetchStoriesCount(); // Refresh stories count when modal closes
          }}
        />
      )}
      {/* Latest Version Modal - Distributor Only */}
      {auth?.role === "distributor" &&
        latestVersions.length > 0 &&
        (() => {
          const currentVersion = latestVersions[latestVersionIndex];
          if (!currentVersion) return null;

          const meta = getTypeMeta(currentVersion.type);
          const hasMany = latestVersions.length > 1;

          const formatDay = (value: any) =>
            new Date(value).toLocaleDateString(undefined, {
              day: "numeric",
              month: "short",
              year: "numeric",
            });

          const scrollArea = {
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.15)",
              borderRadius: 3,
            },
            "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
          } as const;

          const NAV_ROW_H = 42;
          // One colour throughout, taken from the app theme (primary #3C7795).
          const NAV_ACTIVE_BG = "rgba(60, 119, 149, 0.12)";

          return (
            <Modal
              open={latestVersionModalOpen}
              onClose={closeVersionModal}
              closeAfterTransition
              slots={{ backdrop: Backdrop }}
              slotProps={{
                backdrop: {
                  timeout: 220,
                  sx: { backgroundColor: "rgba(16, 24, 40, 0.45)" },
                },
              }}
            >
              <Fade in={latestVersionModalOpen} timeout={220}>
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    // Fixed so it never resizes between announcements, but always
                    // clamped to the viewport so nothing is ever clipped.
                    width: { xs: "94vw", md: 900 },
                    height: { xs: "86vh", md: 540 },
                    maxWidth: "calc(100vw - 32px)",
                    maxHeight: "calc(100vh - 32px)",
                    bgcolor: "#fff",
                    borderRadius: 2,
                    boxShadow: "0 12px 40px rgba(16,24,40,0.18)",
                    outline: "none",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  {/* Masthead: a quiet label, no bar, no rule. */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      px: { xs: 3, md: 5 },
                      pt: { xs: 3, md: 4 },
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: 15.5,
                        color: "text.primary",
                        letterSpacing: -0.1,
                      }}
                    >
                      {t("navbar.latestUpdates")}
                    </Typography>

                    <IconButton
                      onClick={closeVersionModal}
                      size="small"
                      aria-label="Close"
                      sx={{
                        mt: -1,
                        mr: -1,
                        color: "text.disabled",
                        "&:hover": { color: "text.primary", bgcolor: "transparent" },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      flex: 1,
                      minHeight: 0,
                      gap: { xs: 2, md: 5 },
                      px: { xs: 3, md: 5 },
                      pt: { xs: 2.5, md: 3.5 },
                      pb: { xs: 3, md: 4 },
                    }}
                  >
                    {/* Step-list nav: plain text, no rail, no dividers. */}
                    {hasMany && (
                      <Box
                        sx={{
                          flexShrink: 0,
                          width: { xs: "100%", md: 210 },
                          display: "flex",
                          flexDirection: { xs: "row", md: "column" },
                          gap: { xs: 1, md: 0.25 },
                          overflowX: { xs: "auto", md: "visible" },
                          maxHeight: { xs: 52, md: "none" },
                          ...scrollArea,
                        }}
                      >
                        {latestVersions.map((version: any, listIndex: number) => {
                          const itemMeta = getTypeMeta(version.type);
                          const isActive = listIndex === latestVersionIndex;

                          return (
                            <ButtonBase
                              key={version.id ?? listIndex}
                              onClick={() => goToVersion(listIndex)}
                              sx={{
                                position: "relative",
                                flexShrink: 0,
                                justifyContent: "flex-start",
                                width: { xs: "auto", md: "100%" },
                                height: NAV_ROW_H,
                                px: 2,
                                borderRadius: 1,
                                bgcolor: isActive ? NAV_ACTIVE_BG : "transparent",
                                color: isActive ? "primary.main" : "text.secondary",
                                fontSize: 14,
                                fontWeight: isActive ? 700 : 400,
                                textAlign: "left",
                                transition: "background-color 0.15s ease",
                                "&:hover": {
                                  bgcolor: isActive
                                    ? NAV_ACTIVE_BG
                                    : "rgba(0,0,0,0.035)",
                                },
                                // Drupal-style pointer on the active step.
                                "&::after": isActive
                                  ? {
                                      content: '""',
                                      position: "absolute",
                                      right: -9,
                                      top: 0,
                                      width: 0,
                                      height: 0,
                                      borderTop: `${NAV_ROW_H / 2}px solid transparent`,
                                      borderBottom: `${NAV_ROW_H / 2}px solid transparent`,
                                      borderLeft: `9px solid ${NAV_ACTIVE_BG}`,
                                      display: { xs: "none", md: "block" },
                                    }
                                  : undefined,
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  width: 7,
                                  height: 7,
                                  borderRadius: "50%",
                                  bgcolor: "primary.main",
                                  mr: 1.25,
                                  flexShrink: 0,
                                  opacity: isActive ? 1 : 0.55,
                                }}
                              />
                              <Box
                                component="span"
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: { xs: 150, md: "100%" },
                                }}
                              >
                                {version.title || itemMeta.label}
                              </Box>
                            </ButtonBase>
                          );
                        })}
                      </Box>
                    )}

                    {/* Content column, held to a readable measure. */}
                    <Box
                      key={latestVersionIndex}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        pr: { md: 1 },
                        ...scrollArea,
                        ...slideSx,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: 22, md: 27 },
                          lineHeight: 1.2,
                          letterSpacing: -0.5,
                          color: "text.primary",
                          wordBreak: "break-word",
                        }}
                      >
                        {currentVersion.title || t("navbar.newVersionAvailable")}
                      </Typography>

                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mt: 1.25, mb: 2.5 }}
                      >
                        <Box
                          component="span"
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            bgcolor: "primary.main",
                          }}
                        />
                        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                          {meta.label}
                          {currentVersion.createdAt
                            ? `  ·  ${formatDay(currentVersion.createdAt)}`
                            : ""}
                        </Typography>
                      </Stack>

                      {currentVersion.description && (
                        <Box
                          onClick={handleDescriptionClick}
                          sx={{
                            maxWidth: 620,
                            color: "#3d4451",
                            fontSize: 15,
                            lineHeight: 1.75,
                            "& > *:first-of-type": { mt: 0 },
                            "& > *:last-child": { mb: 0 },
                            "& ul, & ol": { pl: 3, mb: 2 },
                            "& li": { mb: 0.75 },
                            "& p": { mb: 1.75 },
                            "& strong, & b": { color: "text.primary", fontWeight: 700 },
                            "& h1, & h2, & h3": {
                              fontSize: 16,
                              fontWeight: 700,
                              color: "text.primary",
                              mt: 3,
                              mb: 1,
                            },
                            "& a": {
                              color: "primary.main",
                              textDecoration: "underline",
                              wordBreak: "break-word",
                            },
                            "& blockquote": {
                              borderLeft: "3px solid",
                              borderColor: "divider",
                              pl: 2,
                              ml: 0,
                              my: 2,
                              color: "text.secondary",
                            },
                            "& img": {
                              display: "inline-block",
                              height: 84,
                              width: "auto",
                              maxWidth: "100%",
                              objectFit: "cover",
                              borderRadius: 1,
                              border: "1px solid",
                              borderColor: "divider",
                              cursor: "zoom-in",
                              verticalAlign: "middle",
                              mr: 1,
                              mb: 1,
                              transition: "transform 0.2s ease, box-shadow 0.2s ease",
                            },
                            "& img:hover": {
                              transform: "scale(1.04)",
                              boxShadow: 3,
                            },
                            "& table": {
                              width: "100%",
                              borderCollapse: "collapse",
                              my: 2,
                            },
                            "& td, & th": { border: "1px solid #eee", p: 1 },
                          }}
                          dangerouslySetInnerHTML={{ __html: currentVersion.description }}
                        />
                      )}

                      {Array.isArray(currentVersion.link) &&
                        currentVersion.link.length > 0 && (
                          <Stack
                            direction="row"
                            flexWrap="wrap"
                            gap={1}
                            sx={{ mt: 3 }}
                          >
                            {currentVersion.link.map((url: string, index: number) => (
                              <Button
                                key={index}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                                variant="outlined"
                                endIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                                sx={{
                                  textTransform: "none",
                                  borderRadius: 1,
                                  fontSize: 13,
                                  maxWidth: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {String(url).replace(/^https?:\/\//, "").slice(0, 40)}
                              </Button>
                            ))}
                          </Stack>
                        )}
                    </Box>
                  </Box>
                </Box>
              </Fade>
            </Modal>
          );
        })()}

      {/* Image lightbox — sits above the announcement modal. Click anywhere,
          or press Esc, to dismiss. */}
      <Modal
        open={Boolean(lightboxSrc)}
        onClose={() => setLightboxSrc(null)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 250,
            sx: { backgroundColor: "rgba(0, 0, 0, 0.88)" },
          },
        }}
        sx={{ zIndex: (t) => t.zIndex.modal + 10 }}
      >
        <Fade in={Boolean(lightboxSrc)} timeout={250}>
          <Box
            onClick={() => setLightboxSrc(null)}
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: 2, sm: 4 },
              outline: "none",
              cursor: "zoom-out",
            }}
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setLightboxSrc(null);
              }}
              aria-label="Close image"
              sx={{
                position: "absolute",
                top: { xs: 10, sm: 18 },
                right: { xs: 10, sm: 18 },
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.12)",
                backdropFilter: "blur(4px)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.24)" },
              }}
            >
              <CloseIcon />
            </IconButton>

            {lightboxSrc && (
              <Box
                component="img"
                src={lightboxSrc}
                alt=""
                // Stop the click so tapping the image itself does not close it,
                // leaving the surrounding area as the dismiss target.
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                sx={{
                  maxWidth: "100%",
                  maxHeight: { xs: "80vh", sm: "88vh" },
                  objectFit: "contain",
                  borderRadius: 2,
                  boxShadow: 24,
                  cursor: "default",
                  backgroundColor: "#fff",
                }}
              />
            )}
          </Box>
        </Fade>
      </Modal>
    </AppBar>
  );
};

export default Navbar;
